import { roleLabels, type AuthUser, type Role } from "@apple-bee/contracts";
import {
  Activity,
  Boxes,
  ChevronDown,
  ClipboardList,
  Database,
  FileSpreadsheet,
  LayoutDashboard,
  LogOut,
  Menu,
  ShieldCheck,
  Users,
  X,
} from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";

export type PageKey =
  | "overview"
  | "inventory"
  | "shipments"
  | "accounting"
  | "prototype-data"
  | "users"
  | "audit";

type NavItem = {
  id: PageKey;
  label: string;
  icon: typeof LayoutDashboard;
  roles: Role[];
};

const navItems: NavItem[] = [
  {
    id: "overview",
    label: "Обзор",
    icon: LayoutDashboard,
    roles: ["CEO", "OPERATIONS", "ACCOUNTANT", "LOGISTICS", "STOREKEEPER", "VIEWER"],
  },
  {
    id: "inventory",
    label: "Остатки",
    icon: Boxes,
    roles: ["CEO", "OPERATIONS", "ACCOUNTANT", "LOGISTICS", "STOREKEEPER", "VIEWER"],
  },
  {
    id: "shipments",
    label: "Заявки и отгрузки",
    icon: ClipboardList,
    roles: ["CEO", "OPERATIONS", "ACCOUNTANT", "LOGISTICS", "STOREKEEPER", "VIEWER"],
  },
  {
    id: "accounting",
    label: "Бухгалтерские файлы",
    icon: FileSpreadsheet,
    roles: ["CEO", "ACCOUNTANT"],
  },
  {
    id: "prototype-data",
    label: "Данные прототипа",
    icon: Database,
    roles: ["CEO", "IT_ADMIN"],
  },
  {
    id: "users",
    label: "Пользователи",
    icon: Users,
    roles: ["CEO", "IT_ADMIN"],
  },
  {
    id: "audit",
    label: "Журнал аудита",
    icon: Activity,
    roles: ["CEO", "IT_ADMIN"],
  },
];

export function allowedPages(user: AuthUser): NavItem[] {
  return navItems.filter((item) =>
    item.roles.some((role) => user.roles.includes(role)),
  );
}

export function AppLayout({
  user,
  currentPage,
  onPageChange,
  onLogout,
  children,
}: {
  user: AuthUser;
  currentPage: PageKey;
  onPageChange: (page: PageKey) => void;
  onLogout: () => Promise<void>;
  children: ReactNode;
}) {
  const [mobileMenu, setMobileMenu] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const visibleItems = useMemo(() => allowedPages(user), [user]);
  const currentLabel =
    visibleItems.find((item) => item.id === currentPage)?.label ?? "Apple Bee";

  function choosePage(page: PageKey) {
    onPageChange(page);
    setMobileMenu(false);
  }

  return (
    <div className="app-shell">
      <aside className={`sidebar ${mobileMenu ? "sidebar--open" : ""}`}>
        <div className="sidebar__top">
          <div className="brand-lockup brand-lockup--light">
            <span className="brand-mark">AB</span>
            <span>
              <strong>Apple Bee</strong>
              <small>Операционный центр</small>
            </span>
          </div>
          <button
            className="icon-button icon-button--light sidebar__close"
            type="button"
            onClick={() => setMobileMenu(false)}
            aria-label="Закрыть меню"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="sidebar__nav" aria-label="Основная навигация">
          <p className="nav-caption">Рабочее пространство</p>
          {visibleItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                className={`nav-item ${currentPage === item.id ? "nav-item--active" : ""}`}
                onClick={() => choosePage(item.id)}
              >
                <Icon size={19} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="sidebar__security">
          <ShieldCheck size={18} />
          <div>
            <strong>Защищённая сессия</strong>
            <span>TOTP и серверные права</span>
          </div>
        </div>
      </aside>

      {mobileMenu ? (
        <button
          className="sidebar-scrim"
          aria-label="Закрыть меню"
          type="button"
          onClick={() => setMobileMenu(false)}
        />
      ) : null}

      <div className="workspace">
        <header className="topbar">
          <div className="topbar__title">
            <button
              className="icon-button topbar__menu"
              type="button"
              onClick={() => setMobileMenu(true)}
              aria-label="Открыть меню"
            >
              <Menu size={22} />
            </button>
            <div>
              <p>Операционный контур</p>
              <h1>{currentLabel}</h1>
            </div>
          </div>
          <div className="profile">
            <button
              className="profile__trigger"
              type="button"
              onClick={() => setProfileOpen((value) => !value)}
              aria-expanded={profileOpen}
            >
              <span className="avatar">
                {user.displayName
                  .split(/\s+/u)
                  .slice(0, 2)
                  .map((part) => part[0])
                  .join("")
                  .toUpperCase()}
              </span>
              <span className="profile__copy">
                <strong>{user.displayName}</strong>
                <small>{user.roles.map((role) => roleLabels[role]).join(" · ")}</small>
              </span>
              <ChevronDown size={17} />
            </button>
            {profileOpen ? (
              <div className="profile-menu">
                <div>
                  <strong>{user.email}</strong>
                  <span>2FA включена</span>
                </div>
                <button
                  type="button"
                  onClick={() => void onLogout()}
                >
                  <LogOut size={17} />
                  Выйти
                </button>
              </div>
            ) : null}
          </div>
        </header>
        <main className="content">{children}</main>
      </div>
    </div>
  );
}
